import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { logger } from '@/utils/logger';
import { useVendors, useUpdateVendorCell, useCreateVendor, useDeleteVendor } from '@/hooks/useVendorCustomer';
import { IVendor } from '@/types/psi';
import { MobileCard, EditableCell } from '@/components/ui';

// Column configuration matching the Suppliers sheet (16 columns A-P)
const COLUMNS = [
  { key: 'vendor_code', labelKey: 'vendor.columns.vendorCode', column: 'A', width: 'w-24' },
  { key: 'vendor_name', labelKey: 'vendor.columns.vendorName', column: 'B', width: 'w-32' },
  { key: 'tax_id', labelKey: 'vendor.columns.taxId', column: 'C', width: 'w-24' },
  { key: 'phone', labelKey: 'vendor.columns.phone', column: 'D', width: 'w-28' },
  { key: 'fax', labelKey: 'vendor.columns.fax', column: 'E', width: 'w-28' },
  { key: 'contact_person', labelKey: 'vendor.columns.contactPerson', column: 'F', width: 'w-24' },
  { key: 'address', labelKey: 'vendor.columns.address', column: 'G', width: 'w-40' },
  { key: 'email', labelKey: 'vendor.columns.email', column: 'H', width: 'w-36' },
  { key: 'mobile', labelKey: 'vendor.columns.mobile', column: 'I', width: 'w-28' },
  { key: 'bank_name', labelKey: 'vendor.columns.bankName', column: 'J', width: 'w-28' },
  { key: 'branch', labelKey: 'vendor.columns.branch', column: 'K', width: 'w-24' },
  { key: 'bank_code', labelKey: 'vendor.columns.bankCode', column: 'L', width: 'w-20' },
  { key: 'account_name', labelKey: 'vendor.columns.accountName', column: 'M', width: 'w-28' },
  { key: 'account_number', labelKey: 'vendor.columns.accountNumber', column: 'N', width: 'w-32' },
  { key: 'currency', labelKey: 'vendor.columns.currency', column: 'O', width: 'w-16' },
  { key: 'vendor_info', labelKey: 'vendor.columns.vendorInfo', column: 'P', width: 'w-40' },
];

const VendorList: React.FC = () => {
  const { t } = useTranslation('psi');
  const { data: vendors, isLoading, error, refetch } = useVendors();
  const updateCell = useUpdateVendorCell();
  const createVendor = useCreateVendor();
  const deleteVendor = useDeleteVendor();

  const [updatingCells, setUpdatingCells] = useState<Set<string>>(new Set());
  const [showAddForm, setShowAddForm] = useState(false);
  const [newVendor, setNewVendor] = useState({
    vendor_code: '',
    vendor_name: '',
    tax_id: '',
    phone: '',
    fax: '',
    contact_person: '',
    address: '',
    email: '',
    mobile: '',
    bank_name: '',
    branch: '',
    bank_code: '',
    account_name: '',
    account_number: '',
    currency: '',
    vendor_info: '',
  });

  const handleCellUpdate = useCallback(async (rowIndex: number, column: string, value: string) => {
    const cellKey = `${rowIndex}-${column}`;
    setUpdatingCells(prev => new Set(prev).add(cellKey));

    try {
      await updateCell.mutateAsync({ row_index: rowIndex, column, value });
    } catch (error) {
      logger.error('Failed to update cell:', error);
    } finally {
      setUpdatingCells(prev => {
        const next = new Set(prev);
        next.delete(cellKey);
        return next;
      });
    }
  }, [updateCell]);

  const handleAddVendor = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createVendor.mutateAsync(newVendor);
      setNewVendor({
        vendor_code: '',
        vendor_name: '',
        tax_id: '',
        phone: '',
        fax: '',
        contact_person: '',
        address: '',
        email: '',
        mobile: '',
        bank_name: '',
        branch: '',
        bank_code: '',
        account_name: '',
        account_number: '',
        currency: '',
        vendor_info: '',
      });
      setShowAddForm(false);
    } catch (error) {
      logger.error('Failed to create vendor:', error);
    }
  };

  const handleDeleteVendor = async (rowIndex: number) => {
    if (window.confirm(t('vendor.confirmDelete'))) {
      try {
        await deleteVendor.mutateAsync(rowIndex);
      } catch (error) {
        logger.error('Failed to delete vendor:', error);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <svg className="animate-spin h-8 w-8 text-indigo-600" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <span className="ml-3 text-gray-600 dark:text-gray-400">{t('vendor.loading')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-700">{t('common.loadError')}</p>
        <button onClick={() => refetch()} className="mt-2 text-red-600 underline">
          {t('common.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('vendor.title')}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('vendor.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => refetch()}
            className="flex-1 sm:flex-none px-3 py-2 text-sm text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 min-h-[44px]"
          >
            {t('common.refresh')}
          </button>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex-1 sm:flex-none px-3 py-2 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 min-h-[44px]"
          >
            {showAddForm ? t('common.cancel') : t('vendor.addVendor')}
          </button>
        </div>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <form onSubmit={handleAddVendor} className="p-3 sm:p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            {COLUMNS.map(col => (
              <div key={col.key}>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{t(col.labelKey)}</label>
                <input
                  type="text"
                  value={newVendor[col.key as keyof typeof newVendor]}
                  onChange={e => setNewVendor({ ...newVendor, [col.key]: e.target.value })}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder={t(col.labelKey)}
                />
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-end">
            <button
              type="submit"
              disabled={createVendor.isLoading}
              className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {createVendor.isLoading ? t('common.adding') : t('vendor.addVendor')}
            </button>
          </div>
        </form>
      )}

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-3">
        {vendors?.map((vendor: IVendor) => (
          <MobileCard
            key={vendor.row_index}
            title={vendor.vendor_name || t('vendor.unnamed')}
            subtitle={vendor.vendor_code}
            fields={[
              { label: t('vendor.columns.contactPerson'), value: vendor.contact_person || '-' },
              { label: t('vendor.columns.phone'), value: vendor.phone || '-' },
              { label: t('vendor.columns.email'), value: vendor.email || '-' },
              { label: t('vendor.columns.address'), value: vendor.address || '-' },
            ]}
            actions={[
              { label: t('common.delete'), onClick: () => handleDeleteVendor(vendor.row_index), variant: 'danger' as const },
            ]}
          />
        ))}
        {(!vendors || vendors.length === 0) && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">{t('vendor.noData')}</div>
        )}
        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
          {t('vendor.editOnDesktop', 'Switch to desktop view for inline editing')}
        </p>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto overflow-y-auto max-h-[600px] border border-gray-200 dark:border-gray-700 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0 z-10">
            <tr>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-10">
                {t('common.row')}
              </th>
              {COLUMNS.map(col => (
                <th
                  key={col.key}
                  className={`px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${col.width}`}
                >
                  {t(col.labelKey)}
                  <span className="text-gray-400 dark:text-gray-500 ml-1">({col.column})</span>
                </th>
              ))}
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-16">
                {t('common.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {vendors?.map((vendor: IVendor) => (
              <tr key={vendor.row_index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-2 py-1 text-xs text-gray-500 dark:text-gray-400">{vendor.row_index}</td>
                {COLUMNS.map(col => (
                  <td key={col.key} className={`px-1 py-1 ${col.width}`}>
                    <EditableCell
                      value={vendor[col.key as keyof IVendor]?.toString() || ''}
                      rowIndex={vendor.row_index}
                      column={col.column}
                      onUpdate={handleCellUpdate}
                      isUpdating={updatingCells.has(`${vendor.row_index}-${col.column}`)}
                    />
                  </td>
                ))}
                <td className="px-2 py-1">
                  <button
                    onClick={() => handleDeleteVendor(vendor.row_index)}
                    className="text-red-600 hover:text-red-800 text-xs"
                    title={t('common.delete')}
                  >
                    {t('common.delete')}
                  </button>
                </td>
              </tr>
            ))}
            {(!vendors || vendors.length === 0) && (
              <tr>
                <td colSpan={COLUMNS.length + 2} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                  {t('vendor.noData')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Status */}
      {updatingCells.size > 0 && (
        <div className="fixed bottom-4 right-4 bg-yellow-100 text-yellow-800 px-4 py-2 rounded-lg shadow-lg">
          {t('vendor.saving')}
        </div>
      )}
    </div>
  );
};

export default VendorList;
